/**
 * `computeProvisionPlan` — calcula o **plano de provisionamento** a
 * partir dos dados comerciais (proposta + plano). Função **pura** que
 * decide:
 *
 *   - Preço SaaS efetivo (após desconto + flag saas_free_during_consulting)
 *   - Dias de trial efetivos (max entre trial explícito e cobertura
 *     da consultoria)
 *   - Se vai criar parcelamento Asaas pra consultoria
 *   - Status inicial da subscription local (`trialing` / `incomplete` /
 *     `active`) conforme Modelo C híbrido (ADR 0009)
 *   - Modo do gate de pagamento (`no_gate` / `first_saas_payment`)
 *
 * **Importante**: existe versão **idêntica** em
 * `supabase/functions/provision-clinic/provision-plan.ts` — cada bundle
 * Deno é isolado, então duplicamos. Mantenha as duas em sincronia
 * (testes em ambos garantem o mesmo comportamento).
 *
 * Casos cobertos (matriz):
 *
 *   | proposal_type      | discount | saas_free | trial | parcl. consult. | resultado                                |
 *   |--------------------|----------|-----------|-------|------------------|------------------------------------------|
 *   | saas               | 0        | —         | 14    | —                | saasPrice=base, trialing 14d, no_gate    |
 *   | saas               | 0        | —         | 0     | —                | saasPrice=base, **incomplete** (saas)    |
 *   | saas               | 50       | —         | 0     | —                | saasPrice=base/2, **incomplete** (saas)  |
 *   | saas_consulting    | 100      | true      | 0     | **N>0**          | saasPrice=0, **incomplete** (consulting) |
 *   | saas_consulting    | 100      | true      | 0     | 0                | saasPrice=0, active, no_gate             |
 *   | saas_consulting    | 0        | false     | 0     | N>0              | saasPrice=base, **incomplete** (saas)    |
 *   | consulting         | —        | —         | —     | N>0              | saasPrice=0, **incomplete** (consulting) |
 *   | consulting         | —        | —         | —     | 0                | saasPrice=0, active, no_gate             |
 *
 * **Mudança 2026-04-26**: combo `saas_free + consulting installments` ANTES
 * caía em `trialing` com trial implícito = duração da consultoria (180d).
 * Resultado: owner ganhava acesso imediato à plataforma sem pagar nada,
 * o que confundia: ele recebia 2 emails (Asaas cobrança + plataforma
 * boas-vindas) ao mesmo tempo, achando o que pagar e podendo entrar.
 *
 * **Decisão de produto** (Alexandre 2026-04-26): consultoria precisa
 * ser PAGA antes de liberar acesso (mesmo que SaaS seja gratuito).
 * Owner só recebe email de ativação após `asaas-webhook`
 * processar PAYMENT_CONFIRMED da primeira parcela. Antes disso, só o
 * email do Asaas com link de pagamento (1 email, fluxo claro).
 *
 * Nova regra: `willChargeConsulting=true` → `'incomplete'` + `gateMode=
 * 'first_consulting_payment'`, independente de `saas_free`. O único caso
 * que cai em `'active'` é `consulting` puro SEM installments (caso
 * degenerado de proposta mal preenchida — não há nada pra cobrar).
 */

export interface ComputeProvisionPlanInput {
  proposal_type: 'saas' | 'consulting' | 'saas_consulting' | string;
  custom_price_brl: number | null;
  discount_percent: number | null;
  trial_days: number | null;
  saas_free_during_consulting: boolean | null;
  consulting_duration_months: number | null;
  consulting_installments_count: number | null;
  consulting_installment_brl: number | null;
  plan_monthly_price_brl: number;
  plan_trial_days: number | null;
}

export interface ProvisionPlan {
  /** Tipo normalizado (saas / consulting / saas_consulting). Tipos
   * desconhecidos caem em 'saas' por segurança. */
  type: 'saas' | 'consulting' | 'saas_consulting';
  /** Preço efetivo do SaaS após desconto + flag saas_free_during_consulting.
   * Quando 0, **não criamos subscription Asaas** (evita cobrança
   * fantasma de R$ 0 e fatura desnecessária). */
  saasMonthlyPrice: number;
  /** Dias efetivos de trial — combina trial_days explícito da proposta
   * com o "trial implícito" gerado por saas_free_during_consulting
   * (que cobre toda a duração da consultoria). */
  effectiveTrialDays: number;
  /** True quando proposta inclui consulting (consulting puro ou combo). */
  hasConsulting: boolean;
  /** True quando vamos criar parcelamento Asaas (sempre que tem
   * consulting, exceto quando installments_count=0 — caso de proposta
   * mal preenchida). */
  willChargeConsulting: boolean;
  consultingInstallmentCount: number;
  consultingInstallmentValue: number;
  consultingTotal: number;
  /** Status inicial da subscription local — decide o gate de pagamento. */
  initialSubscriptionStatus: 'trialing' | 'incomplete' | 'active';
  /** Modo do gate de pagamento (Modelo C). Decide o que o
   * `asaas-webhook` precisa receber pra ativar.
   *
   *   - `no_gate`                  → libera acesso imediato (trialing/active);
   *                                  asaas-webhook não precisa fazer nada
   *   - `first_saas_payment`       → espera PAYMENT_CONFIRMED da subscription
   *                                  SaaS (cobrança recorrente)
   *   - `first_consulting_payment` → espera PAYMENT_CONFIRMED da primeira
   *                                  parcela do parcelamento Asaas
   *                                  (combo saas_free / consulting puro)
   */
  gateMode: 'no_gate' | 'first_saas_payment' | 'first_consulting_payment';
}

export function computeProvisionPlan(input: ComputeProvisionPlanInput): ProvisionPlan {
  const isCombo = input.proposal_type === 'saas_consulting';
  const isConsultingOnly = input.proposal_type === 'consulting';
  const hasConsulting = isCombo || isConsultingOnly;
  const isSaasComponent = isCombo || input.proposal_type === 'saas';

  // 1. Preço SaaS efetivo
  const baseSaasPrice = isSaasComponent
    ? (input.custom_price_brl ?? input.plan_monthly_price_brl ?? 0)
    : 0;
  const discountFactor = 1 - (input.discount_percent ?? 0) / 100;
  let saasMonthlyPrice = Math.round(baseSaasPrice * discountFactor * 100) / 100;
  // Override semântico: combo + saas_free_during_consulting força 0
  // independente do desconto (a regra de negócio é "SaaS coberto pela
  // consultoria"). Mais explícito que confiar em discount_percent=100.
  if (isCombo && input.saas_free_during_consulting === true) {
    saasMonthlyPrice = 0;
  }

  // 2. Trial efetivo (apenas para casos sem cobrança imediata pendente)
  // Pra consulting puro, trial não faz sentido (não há componente SaaS
  // pra "experimentar" — a consultoria é o produto). Ignoramos o trial
  // do plano associado nesse caso.
  const explicitTrial = isSaasComponent ? (input.trial_days ?? input.plan_trial_days ?? 0) : 0;
  // **Mudança 2026-04-26**: removido `impliedTrialFromSaasFree`. Antes,
  // combo `saas_free + consultoria` gerava trial implícito = duração da
  // consultoria (180d), liberando acesso ANTES do owner pagar. Agora
  // consulting installments ATIVAM gate (ver step 4) — owner espera
  // pagamento da 1ª parcela pra liberar.
  const effectiveTrialDays = explicitTrial;

  // 3. Consultoria
  const consultingInstallmentCount = input.consulting_installments_count ?? 0;
  const consultingInstallmentValue = input.consulting_installment_brl ?? 0;
  const consultingTotal =
    Math.round(consultingInstallmentCount * consultingInstallmentValue * 100) / 100;
  const willChargeConsulting = hasConsulting && consultingInstallmentCount > 0;

  // 4. Status inicial + gate de pagamento (Modelo C — ADR 0009).
  //
  // Ordem de prioridade (primeira condição que casar vence):
  //   1. Trial real (`trial_days` > 0) → libera (`trialing`); cobrança
  //      Asaas vai bater só no fim do trial. Sem gate.
  //   2. Consultoria parcelada (`willChargeConsulting`) → gate ativo;
  //      espera 1ª parcela pagar. Cobre tanto combo `saas_free` quanto
  //      `consulting` puro. Decisão de produto: consultoria precisa ser
  //      paga antes de liberar acesso, mesmo que SaaS seja gratuito.
  //   3. Cobrança SaaS recorrente (`saasMonthlyPrice > 0`) sem trial
  //      → gate ativo; espera 1ª fatura SaaS pagar.
  //   4. Senão (SaaS R$ 0 e sem nada pra cobrar) → libera direto
  //      (caso degenerado de proposta sem installments + sem SaaS).
  let initialSubscriptionStatus: ProvisionPlan['initialSubscriptionStatus'];
  let gateMode: ProvisionPlan['gateMode'];
  if (effectiveTrialDays > 0) {
    initialSubscriptionStatus = 'trialing';
    gateMode = 'no_gate';
  } else if (willChargeConsulting) {
    initialSubscriptionStatus = 'incomplete';
    gateMode = 'first_consulting_payment';
  } else if (saasMonthlyPrice > 0) {
    initialSubscriptionStatus = 'incomplete';
    gateMode = 'first_saas_payment';
  } else {
    initialSubscriptionStatus = 'active';
    gateMode = 'no_gate';
  }

  // 5. Type normalizado (defensivo contra strings desconhecidas)
  const type: ProvisionPlan['type'] = isCombo
    ? 'saas_consulting'
    : isConsultingOnly
      ? 'consulting'
      : 'saas';

  return {
    type,
    saasMonthlyPrice,
    effectiveTrialDays,
    hasConsulting,
    willChargeConsulting,
    consultingInstallmentCount,
    consultingInstallmentValue,
    consultingTotal,
    initialSubscriptionStatus,
    gateMode,
  };
}
